import { NumberFormatter } from "@mantine/core";
import { useDisclosure, useHotkeys, useMediaQuery } from "@mantine/hooks";
import * as React from "react";
import type { WorkBook } from "xlsx";
import Breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import FieldSelect from "./components/FieldSelect";
import ItemSearchMenu from "./components/ItemSearchMenu";
import ReturnInfoPanel, { WAREHOUSE_SELECT_ID } from "./components/ReturnInfoPanel";
import ReturnInwardHeading from "./components/ReturnInwardHeading";
import ReturnInwardToolbar from "./components/ReturnInwardToolbar";
import ReturnOrderLinesTable from "./components/ReturnOrderLinesTable";
import ReturnSerialGrid from "./components/ReturnSerialGrid";
import type { ReturnSerialGridHandlers } from "./components/ReturnSerialGrid";
import ScanBar from "./components/ScanBar";
import ExcelSheetModal, { exportReturnSerialsToExcel, pickExcelWorkbook } from "./components/SerialExcel";
import { RETURN_KIND_CONFIG, TOP_BAR_HEIGHT, WARRANTY_POSITION_PATTERN, WARRANTY_WAREHOUSE_FLAG } from "./config/returnKinds";
import type { ReturnSourceType } from "./config/returnKinds";
import { useReturnInwardData } from "./hooks/useReturnInwardData";
import { useReturnInwardSubmit } from "./hooks/useReturnInwardSubmit";
import { useReturnLookups } from "./hooks/useReturnLookups";
import { useReturnPrint } from "./hooks/useReturnPrint";
import { useReturnSerialScanner } from "./hooks/useReturnSerialScanner";
import type { InwardRoute } from "./routing/resolveInwardRoute";
import { createInitialState, returnInwardReducer } from "./state/returnInwardReducer";
import type { RowPosition } from "./state/returnInwardReducer";
import { buildOrderLineView, findPosition, totalScannedQuantity } from "./state/selectors";
import type { PositionOption, ReturnInwardError, ReturnSerialRow, SubmitStatus } from "./types";
import { normalizeSerial } from "./utils/serial";
import { validateReturnInward } from "./validation/validateReturnInward";

interface ReturnInwardPageProps {
  route: InwardRoute;
  sourceType: ReturnSourceType;
}

/** Lỗi không tính là vướng mắc trên nút Hoàn thành (chế độ xem, đã khoá, chưa quét gì) */
const NON_ISSUE_CODES = new Set(["READ_ONLY", "LOCKED", "NO_ROWS"]);

/** Sau khoảng này bỏ yêu cầu cuộn tới dòng, để bấm lại cùng dòng vẫn cuộn được */
const FOCUS_RESET_MS = 2000;
const ORDER_SECTION_HEIGHT = 300;
/** Màn hẹp xếp các khối theo cột, khối "Bên nhận" có chiều cao cố định */
const SMALL_SCREEN_SCAN_SECTION_HEIGHT = 520;
/** Cửa sổ quá thấp thì trang cuộn thay vì ép lưới serial quá bé */
const MIN_PAGE_HEIGHT = 720;
const GRID_HEADER_HEIGHT = 36;
const GRID_FOOTER_HEIGHT = 41;

/**
 * Chiều cao vùng nội dung dưới header của AppShell. Trang kéo tràn cả padding trên và đáy (BLEED) nên phủ kín
 * từ ngay dưới header tới đáy màn hình. Breadcrumb đặt zoom 93% cho body ở các trang nhập kho; 100dvh không co
 * theo zoom nên chia lại cho đủ cao.
 */
const viewportHeight = (zoom: number) => `calc(100dvh / ${zoom} - var(--app-shell-header-offset, 0rem))`;
const BLEED = "calc(var(--app-shell-padding, 0rem) * -1)";

const CARD: React.CSSProperties = {
  boxSizing: "border-box",
  background: "#FFFFFF",
  border: "1px solid #DDE0DA",
  borderRadius: 12,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const CARD_BAR: React.CSSProperties = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "10px 14px",
  borderBottom: "1px solid #ECEEE9",
};

const CARD_TITLE: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "20px",
  color: "#1B1F24",
  whiteSpace: "nowrap",
};

/** Vùng co giãn theo khối cha; nội dung đặt tuyệt đối nên không đẩy ngược kích thước khối */
const FILL_AREA: React.CSSProperties = { flex: "1 1 0", minHeight: 0, position: "relative" };
const FILL_INNER: React.CSSProperties = { position: "absolute", inset: 0, display: "flex", flexDirection: "column" };

const STATUS_LINE: React.CSSProperties = {
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const readDepartmentId = (): number | null => {
  try {
    return JSON.parse(localStorage.getItem("userLogin") || "null")?.depId ?? null;
  } catch {
    return null;
  }
};

const readBodyZoom = () => {
  const zoom = parseFloat(getComputedStyle(document.body).getPropertyValue("zoom"));
  return zoom > 0 ? zoom : 1;
};

/** Zoom hiện tại của body; Breadcrumb đổi zoom theo đường dẫn nên theo dõi thuộc tính style */
const useBodyZoom = () => {
  const [zoom, setZoom] = React.useState(readBodyZoom);
  React.useEffect(() => {
    const update = () => setZoom(readBodyZoom());
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    return () => observer.disconnect();
  }, []);
  return zoom;
};

/** Yêu cầu cuộn tới một dòng; tự bỏ sau FOCUS_RESET_MS */
const useTransientFocus = () => {
  const [value, setValue] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (value === null) return undefined;
    const timer = window.setTimeout(() => setValue(null), FOCUS_RESET_MS);
    return () => window.clearTimeout(timer);
  }, [value]);
  return [value, setValue] as const;
};

const toRowPosition = (position: PositionOption | null | undefined): RowPosition =>
  position ? { id: position.id, code: position.code, name: position.name } : { id: null, code: null, name: null };

const StatusIcon = ({ paths, strokeWidth }: { paths: string[]; strokeWidth: number }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    {paths.map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

/**
 * Trang phiếu nhập đổi trả (NHAP_DOI_TRA).
 * Tách riêng khỏi WarehouseInWardActionV2, giao diện theo trang xuất bán; lưu qua API create-return-in-ward.
 * Mở qua InwardActionDispatcher trên 3 URL nhập kho cũ.
 */
const ReturnInwardPage = ({ route, sourceType }: ReturnInwardPageProps) => {
  const kind = RETURN_KIND_CONFIG[sourceType];
  const isSmallScreen = useMediaQuery("(max-width: 1250px)") ?? false;
  const pageHeight = viewportHeight(useBodyZoom());
  const [state, dispatch] = React.useReducer(returnInwardReducer, undefined, () =>
    createInitialState(sourceType, route.sourceCode || "", readDepartmentId()),
  );
  const { header, order, orderLines, rows, transactionId } = state;

  const readOnly = route.action === "DETAIL" || route.isLocked;
  const locked = header.status === "LOCK";
  const editDisabled = readOnly || locked;

  const { loadError } = useReturnInwardData(route, sourceType, dispatch);
  const { masterOrgs, serialStatuses, warehouses, positions, positionsLoaded } = useReturnLookups({
    branchId: order.toBranchId,
    invId: header.subInvId,
    pickWarehouse: kind.pickWarehouse,
  });

  const lines = React.useMemo(() => buildOrderLineView(orderLines, rows), [orderLines, rows]);
  const totalQuantity = React.useMemo(() => totalScannedQuantity(rows), [rows]);
  const scannedSerials = React.useMemo(() => new Set(rows.map((row) => normalizeSerial(row.serial))), [rows]);
  const rowsRef = React.useRef(rows);
  rowsRef.current = rows;

  // Kho bảo hành: vị trí mặc định là vị trí BHxx_XL và không đổi được, như trang cũ
  const warehouse = warehouses.find((inv) => String(inv.inventoryId) === String(header.subInvId ?? ""));
  const isWarrantyWarehouse = warehouse?.attribute3 === WARRANTY_WAREHOUSE_FLAG;
  const defaultPosition = findPosition(positions, header.toPositionId);

  React.useEffect(() => {
    if (editDisabled || !isWarrantyWarehouse || !positionsLoaded) return;
    const warrantyPosition = positions.find((position) => WARRANTY_POSITION_PATTERN.test(position.code));
    if (warrantyPosition && header.toPositionId !== warrantyPosition.id) {
      dispatch({ type: "SET_DEFAULT_POSITION", position: toRowPosition(warrantyPosition) });
    }
  }, [editDisabled, isWarrantyWarehouse, positionsLoaded, positions, header.toPositionId]);

  const [collapsed, { toggle: toggleCollapse, close: expandPanel }] = useDisclosure(false);
  const [focusSerial, setFocusSerial] = useTransientFocus();
  const [focusOrderSerial, setFocusOrderSerial] = useTransientFocus();

  // "Tới dòng" / "Tới dòng đơn" / "Chọn kho" trong hộp lỗi chặn
  const goToIssue = React.useCallback(
    (error: ReturnInwardError) => {
      if (error.code === "NO_WAREHOUSE") {
        expandPanel();
        window.setTimeout(() => document.getElementById(WAREHOUSE_SELECT_ID)?.focus(), 50);
        return;
      }
      if (error.orderSerial) {
        setFocusOrderSerial(error.orderSerial);
        return;
      }
      if (error.serial) setFocusSerial(error.serial);
    },
    [expandPanel, setFocusOrderSerial, setFocusSerial],
  );

  const { scan, addMany, scanning, lastAdded } = useReturnSerialScanner(state, dispatch, toRowPosition(defaultPosition));
  const { submit, submitting } = useReturnInwardSubmit({ state, dispatch, lines, readOnly, onGoToIssue: goToIssue });
  const print = useReturnPrint(sourceType);

  const [selectedRows, setSelectedRows] = React.useState<ReturnSerialRow[]>([]);
  const [keyword, setKeyword] = React.useState("");
  // Bảng kết quả tìm serial: bấm nút trên header thì đóng, chọn mã ở bảng đơn hàng thì mở lại
  const [searchCloseSignal, setSearchCloseSignal] = React.useState(0);
  const [searchOpenSignal, setSearchOpenSignal] = React.useState(0);
  const pickOrderItem = React.useCallback((itemCode: string) => {
    setKeyword(itemCode);
    setSearchOpenSignal((n) => n + 1);
  }, []);
  const [workbook, setWorkbook] = React.useState<WorkBook | null>(null);

  const onSubmit = React.useCallback((status: SubmitStatus) => submit(status), [submit]);

  useHotkeys([
    // Phím tắt đi qua cùng hàm submit với nút; ở chế độ xem / đã khoá thì bỏ qua như nút bị disable
    ["ctrl+S", () => !editDisabled && submit("OPEN")],
    ["ctrl+Q", () => !editDisabled && submit("LOCK")],
    ["ctrl+shift+Z", () => toggleCollapse()],
  ]);

  const removeRows = React.useCallback((serials: string[]) => {
    dispatch({ type: "REMOVE_ROWS", serials });
    const removed = new Set(serials.map(normalizeSerial));
    setSelectedRows((prev) => prev.filter((row) => !removed.has(normalizeSerial(row.serial))));
  }, []);

  const gridHandlers = React.useMemo<ReturnSerialGridHandlers>(
    () => ({
      onRemove: removeRows,
      onPositionChange: (serial, position) => dispatch({ type: "SET_ROW_POSITION", serial, position: toRowPosition(position) }),
      onFieldChange: (serial, key, value) => dispatch({ type: "SET_ROW_FIELD", serial, key, value }),
      onQuantityChange: (serial, quantity) => dispatch({ type: "SET_ROW_QUANTITY", serial, quantity }),
      onImportExcel: () => pickExcelWorkbook(setWorkbook),
      onExportExcel: () => exportReturnSerialsToExcel(rowsRef.current),
      onSelectionChange: setSelectedRows,
    }),
    [removeRows],
  );

  const handleChangeWarehouse = React.useCallback(
    (invId: number | null) => dispatch({ type: "SET_WAREHOUSE", invId }),
    [],
  );
  const handleChangeDescription = React.useCallback((value: string) => dispatch({ type: "SET_DESCRIPTION", value }), []);
  const handleApplyStatus = React.useCallback((status: string) => dispatch({ type: "SET_ALL_STATUS", status }), []);
  const handleChangeDefaultPosition = React.useCallback(
    (value: string) => dispatch({ type: "SET_DEFAULT_POSITION", position: toRowPosition(findPosition(positions, value)) }),
    [positions],
  );

  // Số vướng mắc nếu hoàn thành lúc này, cùng cách tính với lúc bấm; chỉ hiện khi còn sửa được và đã có serial
  const issueCount = React.useMemo(() => {
    if (editDisabled || rows.length === 0) return 0;
    return validateReturnInward({ status: "LOCK", header, rows, lines, readOnly }).filter(
      (error) => !NON_ISSUE_CODES.has(error.code),
    ).length;
  }, [editDisabled, header, rows, lines, readOnly]);

  // Danh sách vị trí: mã đậm, tên nhạt; gõ mã hoặc tên để lọc
  const positionOptions = React.useMemo(
    () => positions.map((position) => ({ value: String(position.id), label: position.label, primary: position.code, hint: position.name })),
    [positions],
  );

  const hasWarehouse = !!header.subInvId;
  const scanDisabled = editDisabled || !hasWarehouse;
  const positionDisabled = editDisabled || !hasWarehouse || isWarrantyWarehouse;
  const printableId = locked ? transactionId || Number(header.transactionId) || null : null;

  // Dòng trạng thái cạnh ô quét: nhắc chọn kho / serial vừa thêm / chế độ xem
  let statusLine: React.ReactNode = null;
  let statusTitle: string | undefined;
  if (editDisabled) {
    statusLine = (
      <span style={{ ...STATUS_LINE, color: "#5C6570" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          {locked ? "Phiếu đã nhập kho · chỉ xem" : "Phiếu đang ở chế độ xem"}
        </span>
      </span>
    );
  } else if (!hasWarehouse) {
    statusLine = (
      <span style={{ ...STATUS_LINE, color: "#8A4B05" }}>
        <StatusIcon paths={["M12 3a9 9 0 1 0 0 18a9 9 0 1 0 0-18z", "M12 8h.01", "M11 12h1v4h1"]} strokeWidth={2} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          Chọn kho nhập ở khung Thông tin chung để bắt đầu quét serial
        </span>
      </span>
    );
  } else if (lastAdded) {
    statusTitle = [`Đã thêm ${lastAdded.serial}`, lastAdded.remainText, lastAdded.itemName].filter(Boolean).join(" · ");
    statusLine = (
      <span style={{ ...STATUS_LINE, color: "#14532F" }}>
        <StatusIcon paths={["M5 12l5 5L20 7"]} strokeWidth={2.4} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          Đã thêm <b style={{ fontWeight: 600 }}>{lastAdded.serial}</b>
          {lastAdded.remainText && ` · ${lastAdded.remainText}`}
        </span>
      </span>
    );
  } else {
    statusLine = <span style={{ ...STATUS_LINE, color: "#5C6570" }}>Sẵn sàng quét</span>;
  }

  // Màn rộng: các khối ghép sát nhau (không khoảng cách, viền chồng 1px), chỉ bo các góc ngoài.
  // Màn hẹp các khối xếp dọc nên giữ khoảng cách như cũ
  const joined = !isSmallScreen;
  const joinedLeft = joined && !collapsed;

  return (
    <div
      style={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: 8,
        // Kéo nền tràn qua padding bốn cạnh của AppShell để phủ kín vùng nội dung, sát ngay dưới header
        margin: BLEED,
        height: isSmallScreen ? undefined : pageHeight,
        minHeight: isSmallScreen ? pageHeight : MIN_PAGE_HEIGHT,
        background: "#FFFFFF",
        color: "#1B1F24",
        fontSize: 13,
      }}
    >
      <ExcelSheetModal
        workbook={workbook}
        onClose={() => setWorkbook(null)}
        onConfirm={(excelRows) => addMany(excelRows, serialStatuses)}
      />
      {/* Breadcrumb và thanh nút chung một hàng; thanh nút dạt phải */}
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, flexGrow: 1, minWidth: 0 }}>
          <Breadcrumb />
          {/* Loại phiếu nằm ở đầu khối Thông tin chung; khối bị ẩn thì hiện ở đây */}
          {collapsed && <ReturnInwardHeading kindLabel={kind.label} status={header.status} />}
          {loadError && (
            <span role="alert" style={{ color: "#B42318", fontSize: 12, fontWeight: 600 }}>
              Không tải được dữ liệu phiếu.
            </span>
          )}
        </div>
        {/* Bắt sự kiện ở pha capture để đóng bảng tìm serial trước khi nút xử lý; display: contents giữ nguyên bố cục */}
        <div style={{ display: "contents" }} onMouseDownCapture={() => setSearchCloseSignal((n) => n + 1)}>
        <ReturnInwardToolbar
          isSmallScreen={isSmallScreen}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          editDisabled={editDisabled}
          hasRows={rows.length > 0}
          selectedCount={selectedRows.length}
          onDeleteSelected={() => removeRows(selectedRows.map((row) => String(row.serial)))}
          submitting={submitting}
          onSubmit={onSubmit}
          issueCount={issueCount}
          canPrint={kind.printable && printableId !== null}
          printing={print.printing}
          onPrint={() => printableId !== null && print.print(printableId)}
        />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isSmallScreen ? "column" : "row",
          gap: joined ? 0 : 12,
          flex: isSmallScreen ? "none" : "1 1 0",
          minHeight: 0,
        }}
      >
        {!collapsed && (
          <ReturnInfoPanel
            kindLabel={kind.label}
            pickWarehouse={kind.pickWarehouse}
            header={header}
            order={order}
            masterOrgs={masterOrgs}
            warehouses={warehouses}
            serialStatuses={serialStatuses}
            editDisabled={editDisabled}
            hasRows={rows.length > 0}
            onChangeWarehouse={handleChangeWarehouse}
            onChangeDescription={handleChangeDescription}
            onApplyStatus={handleApplyStatus}
            joinedRight={joined}
          />
        )}

        <div
          style={{
            flex: isSmallScreen ? "none" : "1 1 0",
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            gap: joined ? 0 : 12,
            marginLeft: joinedLeft ? -1 : undefined,
          }}
        >
          <section
            aria-label="Bên nhận"
            style={{
              ...CARD,
              flex: isSmallScreen ? "none" : "1 1 0",
              height: isSmallScreen ? SMALL_SCREEN_SCAN_SECTION_HEIGHT : undefined,
              minHeight: 0,
              ...(joined && {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                ...(joinedLeft && { borderTopLeftRadius: 0 }),
              }),
            }}
          >
            <div
              style={{
                ...CARD_BAR,
                flexWrap: "wrap",
                rowGap: 8,
                // Cao bằng thanh Loại phiếu bên trái để tiêu đề lưới thẳng hàng tiêu đề Bên xuất / Bên nhận
                boxSizing: "border-box",
                minHeight: TOP_BAR_HEIGHT,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <h2 style={{ ...CARD_TITLE, marginRight: 6 }}>Bên nhận</h2>
              <FieldSelect
                id="return-inward-default-position"
                caption="Vị trí"
                required
                value={header.toPositionId != null ? String(header.toPositionId) : ""}
                placeholder="Chọn vị trí"
                options={positionOptions}
                disabled={positionDisabled}
                needed={!positionDisabled && header.toPositionId == null}
                width={270}
                captionWidth={58}
                dropdownWidth={360}
                searchable
                title={
                  isWarrantyWarehouse
                    ? "Kho bảo hành tự chọn vị trí BHxx_XL"
                    : "Vị trí cho serial quét tiếp theo; đổi vị trí ở đây thì áp cho mọi serial trên phiếu"
                }
                onChange={handleChangeDefaultPosition}
              />
              <span aria-hidden="true" style={{ width: 1, height: 24, background: "#ECEEE9", flexShrink: 0 }} />
              <ScanBar disabled={scanDisabled} scanning={scanning} onScan={scan} />
              <ItemSearchMenu
                keyword={keyword}
                onKeywordChange={setKeyword}
                lines={orderLines}
                disabled={scanDisabled}
                scannedSerials={scannedSerials}
                onPick={scan}
                closeSignal={searchCloseSignal}
                openSignal={searchOpenSignal}
              />
              <span role="status" title={statusTitle} style={{ display: "flex", flex: "1 1 0", minWidth: 160 }}>
                {statusLine}
              </span>
              {/* Gộp nhãn và số để khi thanh xuống dòng thì cả cụm nằm bên phải */}
              <span style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto", whiteSpace: "nowrap" }}>
                <span style={{ fontWeight: 700 }}>Số lượng:</span>
                <span style={{ color: "#B42318", fontWeight: 700, fontSize: 15 }}>
                  <NumberFormatter value={totalQuantity} thousandSeparator />
                </span>
              </span>
            </div>
            <div style={FILL_AREA}>
              <div style={FILL_INNER}>
                <ReturnSerialGrid
                  rows={rows}
                  orderLines={orderLines}
                  positions={positions}
                  serialStatuses={serialStatuses}
                  editDisabled={editDisabled}
                  focusSerial={focusSerial}
                  {...gridHandlers}
                />
                {rows.length === 0 && (
                  <div
                    style={{
                      position: "absolute",
                      top: GRID_HEADER_HEIGHT,
                      bottom: GRID_FOOTER_HEIGHT,
                      left: 0,
                      right: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 6,
                      padding: 24,
                      textAlign: "center",
                      color: "#5C6570",
                      pointerEvents: "none",
                    }}
                  >
                    <span
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: "#F6F7F4",
                        color: "#A3AAB2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: 4,
                      }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <path d="M4 6v12M7 6v12M11 6v12M14 6v12M18 6v12M20 6v12" />
                      </svg>
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#1B1F24" }}>Chưa có serial nào</span>
                    <span style={{ maxWidth: 420, lineHeight: 1.5 }}>
                      {editDisabled
                        ? "Phiếu chưa có serial."
                        : hasWarehouse
                          ? "Quét mã vạch serial khách trả, hoặc tìm theo mã sản phẩm. Serial phải nằm trong đơn đổi trả."
                          : "Chọn kho nhập ở khung Thông tin chung, sau đó quét mã vạch serial khách trả hoặc tìm theo mã sản phẩm."}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section
            aria-label="Yêu cầu nhập kho"
            style={{
              ...CARD,
              height: ORDER_SECTION_HEIGHT,
              flexShrink: 0,
              ...(joined && {
                marginTop: -1,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
                ...(joinedLeft && { borderBottomLeftRadius: 0 }),
              }),
            }}
          >
            <div style={CARD_BAR}>
              <h2 style={CARD_TITLE}>Yêu cầu nhập kho</h2>
              {header.sourceCode && (
                <span style={{ fontSize: 12, color: "#5C6570", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Đơn đổi trả {header.sourceCode} · hàng khách trả lại
                </span>
              )}
            </div>
            <div style={FILL_AREA}>
              <div style={FILL_INNER}>
                <ReturnOrderLinesTable
                  lines={lines}
                  readOnly={editDisabled}
                  onPickItem={pickOrderItem}
                  focusSerial={focusOrderSerial}
                />
              </div>
            </div>
          </section>
        </div>
      </div>
      {print.printArea}
    </div>
  );
};

export default ReturnInwardPage;
